import {
  Alert,
  Button,
  FormControl,
  LinearProgress,
  TextField,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import Countdown, { zeroPad } from 'react-countdown';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINT } from '../../configurations/global.config';
import { AuthContext } from '../../contexts/user-context/user-context';
import { isMobile, isTablet } from 'react-device-detect';
import CustomDialog from '../common/custom-dialog/custom-dialog';
import { formatAccountId } from './utils';

interface VerifyPhoneProps {
  phoneNumber: string;
  password?: string;
  isOpen: boolean;
  closeVerificationDialog: () => void;
  onSuccess?: () => void;
  account_id: string;
  redirectTo?: string;
}

export const VerifyPhoneDialog = ({
  phoneNumber,
  isOpen,
  closeVerificationDialog,
  password,
  onSuccess,
  account_id,
  redirectTo
}: VerifyPhoneProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendAllowed, setResendAllowed] = useState(false);
  const [resendCountdown, setResendCountdown] = useState<JSX.Element>();
  const [verificationResponse, setVerificationResponse] = useState<AxiosResponse>();
  const [verificationMessage, setVerificationMessage] = useState('');
  const [resendCodeMessage, setResendCodeMessage] = useState('');
  const [verified, setVerified] = useState(false);

  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const resetMessages = () => {
    setVerificationMessage('');
    setResendCodeMessage('');
  };

  const handleResendRequest = async () => {
    resetMessages();
    try {
      const response = await axios.post(`${API_ENDPOINT}/api/auth/resend-code/`, {
        account_id: account_id
      });
      
      if (response.status >= 200 && response.status < 300) {
        setResendCodeMessage('Verification code has been resent');
        setResendAllowed(false);
        startCountdown();
      }
    } catch (e) {
      if (isAxiosError(e)) {
        const response = e.response;
        const errorMessage = response?.data.message || 'Failed to resend code';
        setResendCodeMessage(
          errorMessage === 'account not found' 
            ? 'No account with this number. Please sign up first.'
            : errorMessage === 'account suspended'
            ? 'Account suspended. Please contact support.'
            : errorMessage === 'too many requests'
            ? 'Too many attempts. Please wait 2 minutes.'
            : `Failed to resend code: ${errorMessage}`
        );
      }
    }
  };

  const startCountdown = () => {
    setResendCountdown(
      <Countdown
        date={Date.now() + 2 * 60 * 1000}
        onComplete={() => setResendAllowed(true)}
        renderer={({ minutes, seconds }) => (
          <Typography variant="body2" color="text.secondary">
            Resend available in {zeroPad(minutes)}:{zeroPad(seconds)}
          </Typography>
        )}
      />
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    resetMessages();

    try {
      const response = await axios.post(`${API_ENDPOINT}/api/auth/verify-account/`, {
        account_id: account_id,
        verification_code: verificationCode,
        password: password,
      });
      
      setVerificationResponse(response);
      setVerificationMessage('Account verified successfully!');
      setVerified(true);
      onSuccess?.();
    } catch (e) {
      const errorMessage = isAxiosError(e) 
        ? e.response?.data.message === 'account suspended'
          ? 'Account suspended. Please contact support.'
          : 'Invalid or expired verification code'
        : 'Verification failed. Please try again.';
      
      setVerificationMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsLoading(false);
    setVerificationCode('');
    resetMessages();
    closeVerificationDialog();
  };

  useEffect(() => {
    if (isOpen) {
      startCountdown();
    }
  }, [isOpen]);

  return (
    <CustomDialog 
      fullWidth 
      open={isOpen} 
      onClose={closeVerificationDialog}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }
      }}
    >
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Verify Your Phone
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter the verification code sent to {phoneNumber}
        </Typography>

        {(verificationMessage || resendCodeMessage) && (
          <Alert 
            severity={
              (verificationResponse && verificationResponse?.status>=200 && verificationResponse?.status < 400) || 
              resendCodeMessage.includes('resent') ? 'success' : 'error'
            }
            sx={{ mb: 3, borderRadius: 1 }}
          >
            {verificationMessage || resendCodeMessage}
          </Alert>
        )}

        {!verified ? (
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      disabled={!resendAllowed || isLoading}
                      onClick={handleResendRequest}
                      sx={{
                        textTransform: 'none',
                        minWidth: 'auto',
                        color: resendAllowed ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {resendAllowed ? 'Resend Code' : resendCountdown}
                    </Button>
                  ),
                }}
              />
            </FormControl>

            {isLoading && <LinearProgress sx={{ mb: 2 }} />}

            <Box display="flex" gap={2} mt={2}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                type="submit"
                disabled={isLoading || !verificationCode}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>

              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        ) : (
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={() => {
              setUser(null);
              closeVerificationDialog();
              navigate(`/login${redirectTo ? `?redirect_to=${encodeURIComponent(redirectTo)}` : ''}`);
            }}
            sx={{ borderRadius: 2, fontWeight: 600, mt: 2 }}
          >
            Continue to Login
          </Button>
        )}
      </Box>
    </CustomDialog>
  );
};